/**
 * Preset Thumbnail Cache
 *
 * IndexedDB-based cache for preset thumbnails. Stores auto-captured frames from the output video when saving presets.
 */

const logger = console.scope('PresetThumbnailCache')

// IndexedDB configuration
const DB_NAME = 'oylo-preset-thumbnails'
const DB_VERSION = 3
const STORE_THUMBNAILS = 'thumbnails'
const STORE_PROJECT_CACHE = 'project_cache'

// Special ID for template presets cache
const TEMPLATE_CACHE_ID = 'template_presets'

import { THUMBNAIL_SIZE, THUMBNAIL_MIME, THUMBNAIL_QUALITY } from '~/lib/constants'

// Database instance
let dbInstance = null
let dbInitPromise = null

// In-memory URL cache: presetId -> blobUrl
const urlCache = new Map()

// Listeners for thumbnail updates
const updateListeners = new Set()

/**
 * Initialize the thumbnail cache database
 */
async function openDatabase() {
	if (dbInstance) return dbInstance
	if (dbInitPromise) return dbInitPromise

	dbInitPromise = new Promise((resolve, reject) => {
		const request = indexedDB.open(DB_NAME, DB_VERSION)

		request.onerror = () => {
			logger.error('Failed to open thumbnail cache database:', request.error)
			// Reject first, then clear promise reference to ensure all awaiting callers get the error
			const error = request.error
			reject(error)
			dbInitPromise = null
		}

		request.onsuccess = () => {
			dbInstance = request.result
			dbInstance.onclose = () => {
				dbInstance = null
				dbInitPromise = null
			}
			// Another tab is upgrading the schema: yield, or its open waits on this connection forever.
			dbInstance.onversionchange = () => {
				dbInstance.close()
				dbInstance = null
				dbInitPromise = null
			}
			resolve(dbInstance)
		}

		request.onupgradeneeded = event => {
			const db = event.target.result

			// Create thumbnails store (v1)
			if (!db.objectStoreNames.contains(STORE_THUMBNAILS)) {
				const store = db.createObjectStore(STORE_THUMBNAILS, { keyPath: 'id' })
				store.createIndex('projectId', 'projectId', { unique: false })
				store.createIndex('timestamp', 'timestamp', { unique: false })
			}

			// Create project cache store (v2)
			if (!db.objectStoreNames.contains(STORE_PROJECT_CACHE)) {
				db.createObjectStore(STORE_PROJECT_CACHE, { keyPath: 'id' })
				logger.info('Created project_cache store')
			} else if (event.oldVersion < 3) {
				// v3: cached settings predate the empty migration chain and may hold shapes no reader understands; refetch instead.
				event.target.transaction.objectStore(STORE_PROJECT_CACHE).clear()
				logger.info('Cleared project_cache')
			}
		}
	})

	return dbInitPromise
}

/**
 * Get a thumbnail from cache
 * @param {string} presetId
 * @returns {Promise<{ blob: Blob, url: string } | null>}
 */
export async function getThumbnail(presetId) {
	// Check in-memory URL cache first
	if (urlCache.has(presetId)) {
		return { url: urlCache.get(presetId) }
	}

	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_THUMBNAILS, 'readonly')
			const store = tx.objectStore(STORE_THUMBNAILS)
			const request = store.get(presetId)

			request.onsuccess = () => {
				const record = request.result
				if (!record || !record.blob) {
					resolve(null)
					return
				}

				// Create blob URL and cache it
				const url = URL.createObjectURL(record.blob)
				urlCache.set(presetId, url)
				resolve({ blob: record.blob, url })
			}

			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('getThumbnail error:', err)
		return null
	}
}

/**
 * Store a thumbnail in cache
 * @param {string} presetId
 * @param {string} projectId
 * @param {Blob} blob
 * @param {string} [existingUrl] - Optional existing blob URL to reuse (prevents flicker)
 * @returns {Promise<void>}
 */
export async function setThumbnail(presetId, projectId, blob, existingUrl = null) {
	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_THUMBNAILS, 'readwrite')
			const store = tx.objectStore(STORE_THUMBNAILS)

			const record = {
				id: presetId,
				projectId,
				blob,
				timestamp: Date.now(),
			}

			const request = store.put(record)

			request.onsuccess = () => {
				// Update URL cache Note: Don't revoke old URL immediately - React component may still be using it. Old URLs are cleaned up on project switch via clearUrlCache().
				const oldUrl = urlCache.get(presetId)
				// Reuse existing URL if provided (avoids flicker from URL change)
				const url = existingUrl || URL.createObjectURL(blob)
				urlCache.set(presetId, url)
				logger.info('Thumbnail saved:', presetId)

				// Notify listeners first, then revoke old URL after state can update
				notifyUpdate(presetId, url)

				// Revoke old URL after next paint to allow React to re-render Using requestAnimationFrame is more reliable than setTimeout for this But don't revoke if oldUrl === existingUrl (same URL being reused)
				if (oldUrl && oldUrl !== existingUrl) {
					requestAnimationFrame(() => URL.revokeObjectURL(oldUrl))
				}

				resolve()
			}

			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('setThumbnail error:', err)
	}
}

/**
 * Remove a thumbnail from cache
 * @param {string} presetId
 * @returns {Promise<void>}
 */
export async function removePresetThumbnail(presetId) {
	// Revoke URL if cached - delay to allow React to update with backend URL first
	if (urlCache.has(presetId)) {
		const oldUrl = urlCache.get(presetId)
		urlCache.delete(presetId)
		requestAnimationFrame(() => URL.revokeObjectURL(oldUrl))
	}

	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_THUMBNAILS, 'readwrite')
			const store = tx.objectStore(STORE_THUMBNAILS)
			const request = store.delete(presetId)

			request.onsuccess = () => {
				logger.info('Thumbnail removed:', presetId)
				resolve()
			}
			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('removePresetThumbnail error:', err)
	}
}

/**
 * Clear all thumbnails for a project
 * @param {string} projectId
 * @returns {Promise<void>}
 */
export async function clearProjectThumbnails(projectId) {
	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_THUMBNAILS, 'readwrite')
			const store = tx.objectStore(STORE_THUMBNAILS)
			const index = store.index('projectId')
			const request = index.openCursor(IDBKeyRange.only(projectId))

			request.onsuccess = event => {
				const cursor = event.target.result
				if (cursor) {
					const presetId = cursor.value.id
					// Revoke URL if cached
					if (urlCache.has(presetId)) {
						URL.revokeObjectURL(urlCache.get(presetId))
						urlCache.delete(presetId)
					}
					cursor.delete()
					cursor.continue()
				} else {
					logger.info('Cleared thumbnails for project:', projectId)
					resolve()
				}
			}

			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('clearProjectThumbnails error:', err)
	}
}

/**
 * Clear all in-memory URL cache (call on project switch)
 */
export function clearUrlCache() {
	for (const url of urlCache.values()) {
		URL.revokeObjectURL(url)
	}
	urlCache.clear()
}

/**
 * Subscribe to thumbnail updates
 * @param {function(string, string)} callback - Called with (presetId, url) when thumbnail changes
 * @returns {function} Unsubscribe function
 */
export function onThumbnailUpdate(callback) {
	updateListeners.add(callback)
	return () => updateListeners.delete(callback)
}

/**
 * Notify listeners of a thumbnail update
 */
function notifyUpdate(presetId, url) {
	for (const callback of updateListeners) {
		try {
			callback(presetId, url)
		} catch (err) {
			logger.error('Thumbnail update listener error:', err)
		}
	}
}

/**
 * Load thumbnails for multiple presets Uses backend thumbnailUrl if available, falls back to IndexedDB cache
 * @param {Array<{ id: string, thumbnailUrl?: string }>} presets
 * @param {AbortSignal} [signal] - Optional abort signal to cancel loading
 * @returns {Promise<Map<string, string>>} Map of presetId -> url
 */
export async function loadThumbnails(presets, signal) {
	const result = new Map()

	for (const preset of presets) {
		// Check if aborted before each iteration
		if (signal?.aborted) {
			return result
		}

		// Prefer backend URL if available (stable CDN URL)
		if (preset.thumbnailUrl) {
			result.set(preset.id, preset.thumbnailUrl)
			continue
		}

		// Fall back to IndexedDB cache
		const thumb = await getThumbnail(preset.id)
		if (thumb?.url) {
			result.set(preset.id, thumb.url)
		}
	}

	return result
}

/**
 * Capture a thumbnail from a video element
 * @param {HTMLVideoElement} videoElement
 * @returns {Promise<Blob | null>}
 */
export async function captureVideoThumbnail(videoElement) {
	if (!videoElement || !videoElement.videoWidth || !videoElement.videoHeight) {
		logger.warn('Cannot capture thumbnail: video not ready')
		return null
	}

	try {
		const canvas = document.createElement('canvas')
		const ctx = canvas.getContext('2d')

		// Center-crop to square, then scale to THUMBNAIL_SIZE
		const srcW = videoElement.videoWidth
		const srcH = videoElement.videoHeight
		const cropSize = Math.min(srcW, srcH)
		const sx = (srcW - cropSize) / 2
		const sy = (srcH - cropSize) / 2

		canvas.width = THUMBNAIL_SIZE
		canvas.height = THUMBNAIL_SIZE

		ctx.drawImage(videoElement, sx, sy, cropSize, cropSize, 0, 0, THUMBNAIL_SIZE, THUMBNAIL_SIZE)

		return new Promise(resolve => {
			canvas.toBlob(
				blob => {
					if (blob) {
						logger.info(
							`Thumbnail captured: ${THUMBNAIL_SIZE}x${THUMBNAIL_SIZE}, ${Math.round(blob.size / 1024)}KB`
						)
					}
					resolve(blob)
				},
				THUMBNAIL_MIME,
				THUMBNAIL_QUALITY
			)
		})
	} catch (err) {
		logger.error('captureVideoThumbnail error:', err)
		return null
	}
}

/**
 * Capture a thumbnail from a canvas element (e.g. PixiJS canvas)
 * @param {HTMLCanvasElement} canvas
 * @returns {Promise<Blob | null>}
 */
export async function captureCanvasThumbnail(canvas) {
	if (!canvas || !canvas.width || !canvas.height) {
		logger.warn('Cannot capture thumbnail: canvas not ready')
		return null
	}

	try {
		const offscreen = document.createElement('canvas')
		const ctx = offscreen.getContext('2d')

		// Center-crop to square, then scale to THUMBNAIL_SIZE
		const srcW = canvas.width
		const srcH = canvas.height
		const cropSize = Math.min(srcW, srcH)
		const sx = (srcW - cropSize) / 2
		const sy = (srcH - cropSize) / 2

		offscreen.width = THUMBNAIL_SIZE
		offscreen.height = THUMBNAIL_SIZE

		ctx.drawImage(canvas, sx, sy, cropSize, cropSize, 0, 0, THUMBNAIL_SIZE, THUMBNAIL_SIZE)

		return new Promise(resolve => {
			offscreen.toBlob(
				blob => {
					if (blob) {
						logger.info(
							`Canvas thumbnail captured: ${THUMBNAIL_SIZE}x${THUMBNAIL_SIZE}, ${Math.round(blob.size / 1024)}KB`
						)
					}
					resolve(blob)
				},
				THUMBNAIL_MIME,
				THUMBNAIL_QUALITY
			)
		})
	} catch (err) {
		logger.error('captureCanvasThumbnail error:', err)
		return null
	}
}

/**
 * Capture and save a thumbnail for a preset
 * @param {string} presetId
 * @param {string} projectId
 * @returns {Promise<boolean>} Success
 */
export async function captureAndSaveThumbnail(presetId, projectId) {
	const videoElement = window.output_video

	if (!videoElement) {
		logger.warn('Cannot capture: output_video not available')
		return false
	}

	const blob = await captureVideoThumbnail(videoElement)
	if (!blob) {
		return false
	}

	await setThumbnail(presetId, projectId, blob)
	return true
}

// ============================================================================
// Project Cache Functions (for instant preset loading)
// ============================================================================

/**
 * Get cached project data
 * @param {string} projectId
 * @returns {Promise<{ data: object, updatedAt: string, cachedAt: number } | null>}
 */
export async function getProjectCache(projectId) {
	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_PROJECT_CACHE, 'readonly')
			const store = tx.objectStore(STORE_PROJECT_CACHE)
			const request = store.get(projectId)

			request.onsuccess = () => {
				const record = request.result
				if (!record || !record.data) {
					resolve(null)
					return
				}
				resolve({
					data: record.data,
					updatedAt: record.updatedAt,
					cachedAt: record.cachedAt,
				})
			}

			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('getProjectCache error:', err)
		return null
	}
}

/**
 * Store project data in cache
 * @param {string} projectId
 * @param {object} data - { project, presets }
 * @param {string} updatedAt - Server's updatedAt timestamp
 * @returns {Promise<void>}
 */
export async function setProjectCache(projectId, data, updatedAt) {
	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_PROJECT_CACHE, 'readwrite')
			const store = tx.objectStore(STORE_PROJECT_CACHE)

			const record = {
				id: projectId,
				data,
				updatedAt,
				cachedAt: Date.now(),
			}

			const request = store.put(record)

			request.onsuccess = () => {
				logger.info('Project cache saved:', projectId)
				resolve()
			}

			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('setProjectCache error:', err)
	}
}

/**
 * Get cached template presets
 * @returns {Promise<object[] | null>}
 */
export async function getTemplateCache() {
	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_PROJECT_CACHE, 'readonly')
			const store = tx.objectStore(STORE_PROJECT_CACHE)
			const request = store.get(TEMPLATE_CACHE_ID)

			request.onsuccess = () => {
				const record = request.result
				if (!record || !record.data) {
					resolve(null)
					return
				}
				resolve(record.data)
			}

			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('getTemplateCache error:', err)
		return null
	}
}

/**
 * Store template presets in cache
 * @param {object[]} presets - Array of template presets
 * @returns {Promise<void>}
 */
export async function setTemplateCache(presets) {
	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_PROJECT_CACHE, 'readwrite')
			const store = tx.objectStore(STORE_PROJECT_CACHE)

			const record = {
				id: TEMPLATE_CACHE_ID,
				data: presets,
				cachedAt: Date.now(),
			}

			const request = store.put(record)

			request.onsuccess = () => {
				logger.info('Template cache saved:', presets?.length, 'presets')
				resolve()
			}

			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('setTemplateCache error:', err)
	}
}

/**
 * Clear cached project data
 * @param {string} projectId
 * @returns {Promise<void>}
 */
export async function clearProjectCache(projectId) {
	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_PROJECT_CACHE, 'readwrite')
			const store = tx.objectStore(STORE_PROJECT_CACHE)
			const request = store.delete(projectId)

			request.onsuccess = () => {
				logger.info('Project cache cleared:', projectId)
				resolve()
			}

			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('clearProjectCache error:', err)
	}
}

/**
 * Clear all cached data for a project (thumbnails + project cache) Use on logout or project reset
 * @param {string} projectId
 * @returns {Promise<void>}
 */
export async function clearAllProjectData(projectId) {
	await clearProjectThumbnails(projectId)
	await clearProjectCache(projectId)
	clearUrlCache()
	logger.info('Cleared all project data:', projectId)
}

// ============================================================================
// Desktop Template Cache Functions (for instant desktop example preset loading)
// ============================================================================

const DESKTOP_TEMPLATE_CACHE_ID = 'desktop_template_presets'

/**
 * Get cached desktop template presets
 * @returns {Promise<object[] | null>}
 */
export async function getDesktopTemplateCache() {
	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_PROJECT_CACHE, 'readonly')
			const store = tx.objectStore(STORE_PROJECT_CACHE)
			const request = store.get(DESKTOP_TEMPLATE_CACHE_ID)

			request.onsuccess = () => {
				const record = request.result
				if (!record || !record.data) {
					resolve(null)
					return
				}
				resolve(record.data)
			}

			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('getDesktopTemplateCache error:', err)
		return null
	}
}

/**
 * Store desktop template presets in cache
 * @param {object[]} presets - Array of desktop template presets
 * @returns {Promise<void>}
 */
export async function setDesktopTemplateCache(presets) {
	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_PROJECT_CACHE, 'readwrite')
			const store = tx.objectStore(STORE_PROJECT_CACHE)

			const record = {
				id: DESKTOP_TEMPLATE_CACHE_ID,
				data: presets,
				cachedAt: Date.now(),
			}

			const request = store.put(record)

			request.onsuccess = () => {
				logger.info('Desktop template cache saved:', presets?.length, 'presets')
				resolve()
			}

			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('setDesktopTemplateCache error:', err)
	}
}
